import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import getDataTableStyles from '../../utils/tableStyles';
import { Alert, Button, Spinner, Modal, Form, InputGroup, Card, Row, Col, Badge, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axiosInstance from '../../api/axios';
import PhotoAlbum from 'react-photo-album';
import { useTranslation } from 'react-i18next';

function Catalogue() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const customStyles = getDataTableStyles(isDark);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Forms
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const initialForm = {
    sku: '',
    nombre: '',
    descripcion: '',
    familia: '',
    marca: '',
    precio_base_usd: '',
    unidades_por_caja: 1,
    stock_real: '',
    stock_minimo: 0,
    stock_reservado: 0,
    stock_visible: true,
    categoria_id: '',
    unidad_medida: 'UND',
    imagen_base64: '',
    imagen_url_existente: '',
    imagen_b2c_url: '',
    imagen_b2b_url: '',
    galeria: []
  };
  const [formData, setFormData] = useState(initialForm);
  const [importFile, setImportFile] = useState(null);
  const [showImportErrorModal, setShowImportErrorModal] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const handleCategoryChange = (catId) => {
    const selectedCat = categories.find(c => c.id === catId);
    const prefix = selectedCat?.prefijo_sku ? `${selectedCat.prefijo_sku}-` : 'A-';
    
    // 1. Immediate state update for instant UI feedback (0ms)
    setFormData(prev => ({
      ...prev,
      categoria_id: catId,
      sku: (!editingId && catId) ? prefix : prev.sku
    }));

    // 2. Fetch exact next SKU from Backend asynchronously in background
    if (!editingId && catId) {
      axiosInstance.get(`/admin/catalog/next-sku?categoria_id=${catId}`)
        .then(response => {
          if (response.data?.success && response.data?.next_sku) {
            setFormData(prev => ({ ...prev, sku: response.data.next_sku }));
          }
        })
        .catch(() => {});
    }
  };

  // Gallery state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [gallerySearch, setGallerySearch] = useState('');
  const [galleryTargetField, setGalleryTargetField] = useState('imagen_b2c_url');
  const [galleryProductFilter, setGalleryProductFilter] = useState(''); // '' = Todos, 'current' = Solo de este producto, 'reserva' = En reserva, 'unassigned' = Disponibles
  const galleryFileInputRef = useRef(null);

  // Filters
  const [filterActive, setFilterActive] = useState(''); // '' = Todos, 'true' = Activos, 'false' = Inactivos
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/admin/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando categorias', err);
    }
  };

  const fetchFamilies = async () => {
    try {
      const response = await axiosInstance.get('/admin/catalog/families');
      if (response.data.success) {
        setFamilies(response.data.data || []);
      }
    } catch (err) {
      console.error('Error cargando familias', err);
    }
  };

  const fetchProducts = async (page = currentPage, currentPerPage = perPage) => {
    try {
      setLoading(true);
      let params = [];
      params.push(`page=${page}`);
      params.push(`per_page=${currentPerPage}`);
      if (filterActive !== '') params.push(`is_active=${filterActive}`);
      if (filterCategory !== '') params.push(`categoria_id=${filterCategory}`);
      if (filterFamily !== '') params.push(`familia=${encodeURIComponent(filterFamily)}`);
      if (filterSearch !== '') params.push(`search=${encodeURIComponent(filterSearch)}`);
      const query = params.length > 0 ? `?${params.join('&')}` : '';

      const response = await axiosInstance.get(`/admin/catalog${query}`);
      if (response.data.success) {
        setProducts(response.data.data);
        if (response.data.meta) {
          setTotalRows(response.data.meta.total !== undefined ? response.data.meta.total : response.data.data.length);
        }
      }
    } catch (err) {
      setError('Ocurrió un error al cargar el inventario.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      setLoadingGallery(true);
      const response = await axiosInstance.get('/admin/catalog/gallery');
      if (response.data.success) {
        setGalleryImages(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando galería', err);
    } finally {
      setLoadingGallery(false);
    }
  };

  const openGallery = (field = 'imagen_b2c_url') => {
    setGalleryTargetField(field);
    setGallerySearch('');
    if (editingId) {
      setGalleryProductFilter('current');
    } else {
      setGalleryProductFilter('');
    }
    setShowGalleryModal(true);
    fetchGallery();
  };

  // Promover una foto de reserva a Principal B2C (la anterior pasa a la reserva)
  const handlePromoteToB2c = (imageSrc) => {
    setFormData(prev => {
      const currentB2c = prev.imagen_b2c_url;
      const newGaleria = (prev.galeria || []).filter(img => img !== imageSrc);
      if (currentB2c && currentB2c !== imageSrc && !newGaleria.includes(currentB2c)) {
        newGaleria.unshift(currentB2c);
      }
      return {
        ...prev,
        imagen_b2c_url: imageSrc,
        imagen_url_existente: prev.imagen_url_existente || imageSrc,
        galeria: newGaleria
      };
    });
  };

  // Promover una foto de reserva a Principal B2B (la anterior pasa a la reserva)
  const handlePromoteToB2b = (imageSrc) => {
    setFormData(prev => {
      const currentB2b = prev.imagen_b2b_url;
      const newGaleria = (prev.galeria || []).filter(img => img !== imageSrc);
      if (currentB2b && currentB2b !== imageSrc && !newGaleria.includes(currentB2b)) {
        newGaleria.unshift(currentB2b);
      }
      return {
        ...prev,
        imagen_b2b_url: imageSrc,
        galeria: newGaleria
      };
    });
  };

  // Mover foto de B2C a la Reserva del producto
  const handleMoveB2cToReserva = () => {
    if (!formData.imagen_b2c_url) return;
    setFormData(prev => {
      const currentGaleria = Array.isArray(prev.galeria) ? [...prev.galeria] : [];
      if (!currentGaleria.includes(prev.imagen_b2c_url)) {
        currentGaleria.push(prev.imagen_b2c_url);
      }
      return {
        ...prev,
        imagen_b2c_url: '',
        galeria: currentGaleria
      };
    });
  };

  // Mover foto de B2B a la Reserva del producto
  const handleMoveB2bToReserva = () => {
    if (!formData.imagen_b2b_url) return;
    setFormData(prev => {
      const currentGaleria = Array.isArray(prev.galeria) ? [...prev.galeria] : [];
      if (!currentGaleria.includes(prev.imagen_b2b_url)) {
        currentGaleria.push(prev.imagen_b2b_url);
      }
      return {
        ...prev,
        imagen_b2b_url: '',
        galeria: currentGaleria
      };
    });
  };

  // Quitar una foto de la Reserva
  const handleRemoveFromReserva = (imageSrc) => {
    setFormData(prev => ({
      ...prev,
      galeria: (prev.galeria || []).filter(img => img !== imageSrc)
    }));
  };

  // Añadir una foto a la Reserva
  const handleAddToReserva = (imageSrc) => {
    setFormData(prev => {
      const currentGaleria = Array.isArray(prev.galeria) ? [...prev.galeria] : [];
      if (!currentGaleria.includes(imageSrc)) {
        currentGaleria.push(imageSrc);
      }
      return {
        ...prev,
        galeria: currentGaleria
      };
    });
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingGallery(true);
    const uploadData = new FormData();
    for (let i = 0; i < files.length; i++) {
      uploadData.append('images[]', files[i]);
    }

    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        fetchGallery();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error subiendo imagen a la galería.');
    } finally {
      setUploadingGallery(false);
      if (galleryFileInputRef.current) galleryFileInputRef.current.value = '';
    }
  };

  const selectGalleryImage = (img, explicitAction = null) => {
    const imgSrc = typeof img === 'string' ? img : img.src;
    const assigned = typeof img === 'object' ? img.assigned_to : null;
    if (assigned && assigned.id !== editingId) {
      alert(`Esta imagen ya está asignada al producto "${assigned.nombre}" (SKU: ${assigned.sku}). Las imágenes deben ser exclusivas de cada producto.`);
      return;
    }

    const action = explicitAction || galleryTargetField;

    if (action === 'imagen_b2c_url' || action === 'b2c') {
      handlePromoteToB2c(imgSrc);
    } else if (action === 'imagen_b2b_url' || action === 'b2b') {
      handlePromoteToB2b(imgSrc);
    } else if (action === 'galeria_add' || action === 'reserva') {
      handleAddToReserva(imgSrc);
    } else {
      setFormData(prev => ({
        ...prev,
        [galleryTargetField]: imgSrc,
        ...(galleryTargetField === 'imagen_url_existente' ? { imagen_base64: '' } : {})
      }));
    }
    setShowGalleryModal(false);
  };

  useEffect(() => {
    fetchCategories();
    fetchFamilies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    if (!filterSearch) {
      fetchProducts(1, perPage);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      fetchProducts(1, perPage);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [filterActive, filterCategory, filterFamily, filterSearch]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const payload = { 
        ...formData, 
        is_active: true,
        imagen_url: formData.imagen_b2c_url || formData.imagen_b2b_url || ''
      };
      
      let response;
      if (editingId) {
        response = await axiosInstance.put(`/admin/catalog/${editingId}`, payload);
      } else {
        response = await axiosInstance.post('/admin/catalog', payload);
      }

      if (response.data.success) {
        setShowCreateModal(false);
        setEditingId(null);
        setFormData(initialForm);
        fetchProducts(); // refresh list
        fetchFamilies(); // refresh families if new one added
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando producto. Revisa los datos.');
    } finally {
      setProcessing(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(initialForm);
    setShowCreateModal(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setFormData({
      sku: row.sku || '',
      nombre: row.nombre || '',
      descripcion: row.descripcion || '',
      familia: row.familia || '',
      marca: row.marca || '',
      precio_base_usd: row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : ''),
      precio_b_usd: row.precio_b_usd || (row.precios && row.precios.length > 1 ? row.precios[1].amount : ''),
      unidades_por_caja: row.unidades_por_caja || 1,
      stock_real: row.stock_real || 0,
      stock_minimo: row.stock_minimo || 0,
      stock_reservado: row.stock_reservado || 0,
      stock_visible: row.stock_visible !== undefined ? row.stock_visible : true,
      categoria_id: row.categoria_id || '',
      unidad_medida: row.unidad_medida || 'UND',
      imagen_base64: '',
      imagen_url_existente: row.imagen_url || '',
      imagen_b2c_url: row.imagen_b2c_url || '',
      imagen_b2b_url: row.imagen_b2b_url || '',
      galeria: row.galeria_imagenes || (Array.isArray(row.galeria) ? row.galeria : [])
    });
    setShowCreateModal(true);
  };

  // Download official excel template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/admin/catalog/template', {
        responseType: 'blob'
      });
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_carga_masiva_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar plantilla:', err);
      alert('No se pudo descargar la plantilla oficial.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setImportFile(file);
      } else {
        alert('Formato no soportado. Por favor sube un archivo Excel (.xlsx, .xls) o CSV.');
      }
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return alert('Por favor selecciona un archivo Excel (.xlsx) o CSV primero.');
    
    setProcessing(true);
    const data = new FormData();
    data.append('file', importFile);

    try {
      const response = await axiosInstance.post('/admin/catalog/import', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        alert(response.data.message || 'Productos importados con éxito.');
        setShowImportModal(false);
        setImportFile(null);
        fetchProducts(1, perPage);
      }
    } catch (err) {
      console.error('Error al importar productos:', err);
      const errorData = err.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        setImportErrors(errorData.errors);
        setShowImportModal(false);
        setShowImportErrorModal(true);
      } else {
        alert('Error al importar productos: ' + (errorData?.message || err.message));
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (id) => {
    if(!window.confirm("¿Seguro que deseas dar de baja este producto? Ya no aparecerá disponible para los vendedores.")) return;
    try {
      await axiosInstance.delete(`/admin/catalog/${id}`);
      fetchProducts();
    } catch (err) {
      alert('Error al desactivar el producto.');
    }
  };

  const columns = [
    {
      name: t('th_img', 'Img'),
      selector: row => row.imagen_url,
      width: '60px',
      cell: row => {
        if (!row.imagen_url) {
          return <div style={{width: '32px', height: '32px', background: '#eee', borderRadius: '4px'}} className="d-flex align-items-center justify-content-center"><i className="mdi mdi-image text-muted"></i></div>;
        }
        return (
          <img 
            src={row.imagen_url} 
            alt="prod" 
            style={{width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px'}} 
          />
        );
      }
    },
    {
      name: 'SKU',
      selector: row => row.sku,
      sortable: true,
      minWidth: '120px'
    },
    {
      name: t('th_name', 'Nombre'),
      selector: row => row.nombre,
      sortable: true,
      minWidth: '200px'
    },
    {
      name: t('th_price', 'Precio ($)'),
      selector: row => row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : 0),
      sortable: true,
      minWidth: '130px',
      cell: row => {
        const p = row.precio_base_usd || (row.precios && row.precios.length > 0 ? row.precios[0].amount : 0);
        return <span className="fw-bold text-primary">${parseFloat(p || 0).toFixed(2)}</span>;
      }
    },
    {
      name: t('units_per_box_badge', 'Und/Caja'),
      selector: row => row.unidades_por_caja || 1,
      sortable: true,
      width: '100px',
      cell: row => <span className="badge bg-secondary">{row.unidades_por_caja || 1} {t('units_per_box_badge', 'u/caja')}</span>
    },
    {
      name: t('th_category', 'Categoría'),
      selector: row => row.category ? row.category.nombre : t('no_category', 'Sin Categoría'),
      sortable: true,
      minWidth: '150px'
    },
    {
      name: t('th_family_brand', 'Familia / Marca'),
      selector: row => row.familia || row.marca || '',
      sortable: true,
      minWidth: '180px',
      cell: row => (
        <div className="py-1">
          {row.familia && (
            <div className="mb-1">
              <span className="badge" style={{ backgroundColor: '#183eeb', color: '#fff', fontSize: '0.75rem', fontWeight: '600' }}>
                <i className="mdi mdi-factory me-1"></i>{row.familia}
              </span>
            </div>
          )}
          {row.marca && row.marca !== row.familia && (
            <div>
              <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                <i className="mdi mdi-tag-outline me-1 text-muted"></i>{row.marca}
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      name: t('real_stock', 'Stock Real'),
      selector: row => row.stock_real,
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const stock = row.stock_real ?? 0;
        const minStock = row.stock_minimo ?? 0;
        if (stock === 0) {
          return (
            <div className="py-1">
              <span className="badge bg-danger p-2">
                <i className="mdi mdi-alert-circle me-1"></i>{t('stock_out_of_stock', 'Agotado (0)')}
              </span>
              <small className="text-muted d-block mt-1" style={{ fontSize: '0.7rem' }}>
                <i className="mdi mdi-eye-off me-1"></i>{t('stock_hidden_in_app', 'Oculto en App')}
              </small>
            </div>
          );
        }
        if (stock <= minStock) {
          return (
            <span className="badge bg-warning text-dark p-2">
              <i className="mdi mdi-alert me-1"></i>{t('stock_low', 'Bajo')} ({stock} {row.unidad_medida || 'UND'})
            </span>
          );
        }
        return (
          <span className="badge bg-success p-2">
            <i className="mdi mdi-check-circle me-1"></i>{stock} {row.unidad_medida || 'UND'}
          </span>
        );
      }
    },
    {
      name: t('reserved_stock', 'Reservado'),
      selector: row => row.stock_reservado,
      sortable: true,
      width: '100px',
      cell: row => <span className="text-warning">{row.stock_reservado}</span>
    },
    {
      name: t('th_status', 'Estado'),
      selector: row => row.is_active,
      width: '120px',
      cell: row => (
        row.is_active 
          ? <label className="badge bg-success">{t('status_active', 'Activo')}</label>
          : <label className="badge bg-danger">{t('status_inactive', 'Inactivo')}</label>
      )
    },
    {
      name: t('th_actions', 'Acciones'),
      width: '120px',
      minWidth: '100px',
      cell: row => (
        <div className="d-flex align-items-center gap-1">
          <button 
            className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center" 
            style={{ width: '32px', height: '32px', padding: 0 }} 
            onClick={() => openEditModal(row)}
            title={t('btn_edit_product', 'Editar Producto')}
          >
            <i className="mdi mdi-pencil" style={{ fontSize: '1.05rem' }}></i>
          </button>
          {row.is_active ? (
            <button 
              className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row.id)}
              title={t('btn_deactivate', 'Dar de Baja')}
            >
              <i className="mdi mdi-delete-outline" style={{ fontSize: '1.1rem' }}></i>
            </button>
          ) : (
            <button 
              className="btn btn-sm btn-outline-success rounded-circle d-flex align-items-center justify-content-center" 
              style={{ width: '32px', height: '32px', padding: 0 }} 
              onClick={() => handleToggleStatus(row.id)}
              title={t('btn_activate_product', 'Reactivar Producto')}
            >
              <i className="mdi mdi-check-circle-outline" style={{ fontSize: '1.1rem' }}></i>
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className='page-header'>
        <h3 className='page-title'>{t('inventory_management', 'Gestión de Inventario')}</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>{t('menu_sales_catalog', 'Catálogo')}</a></li>
            <li className="breadcrumb-item active" aria-current="page">{t('menu_product_catalog', 'Inventario')}</li>
          </ol>
        </nav>
      </div>

      <div className='row'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-body'>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className='card-title mb-0'>{t('filters_and_search', 'Filtros y Búsqueda')}</h4>
              </div>
              <div className="row mb-2">
                <div className="col-md-3 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('search_product_label', 'Buscar Producto')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder={t('search_product_placeholder', 'Buscar por Nombre, SKU, Marca o Familia...')} 
                      value={filterSearch} 
                      onChange={e => setFilterSearch(e.target.value)} 
                      className="customer-filter-control w-100 shadow-none"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_status', 'Estatus')}</Form.Label>
                    <Form.Select 
                      value={filterActive} 
                      onChange={e => setFilterActive(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('status_all', 'Todos los Estados')}</option>
                      <option value="true">{t('status_active_in_catalog', 'Activos (En catálogo)')}</option>
                      <option value="false">{t('status_inactive_disabled', 'Inactivos (Baja)')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-4 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_category', 'Categoría / Departamento')}</Form.Label>
                    <Form.Select 
                      value={filterCategory} 
                      onChange={e => setFilterCategory(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('all_categories', 'Todas las Categorías')}</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.parent_id ? `\u00A0\u00A0↳ ${c.nombre} (${c.prefijo_sku || ''})` : `📁 ${c.nombre.toUpperCase()}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <Form.Group>
                    <Form.Label className="fw-bold small mb-1">{t('th_family_manufacturer', 'Familia (Fabricante)')}</Form.Label>
                    <Form.Select 
                      value={filterFamily} 
                      onChange={e => setFilterFamily(e.target.value)}
                      className="customer-filter-control w-100 cursor-pointer shadow-none"
                    >
                      <option value="">{t('all_families', 'Todas las Familias')}</option>
                      {families.map((fam, idx) => (
                        <option key={idx} value={fam}>
                          🏭 {fam}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-body'>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <h4 className='card-title mb-0'>{t('products_and_stock', 'Productos y Stock')}</h4>
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => setShowImportModal(true)}
                    style={{ borderRadius: '10px' }}
                  >
                    <i className="mdi mdi-file-excel me-1"></i>{t('btn_import_excel', 'Importar Masiva')}
                  </Button>
                  <Button 
                    style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }} 
                    onClick={openCreateModal}
                  >
                    <i className="mdi mdi-plus me-1"></i>{t('btn_add_product', 'Nuevo Producto')}
                  </Button>
                </div>
              </div>
              
              {error && <Alert variant='danger'>{error}</Alert>}
              
              <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                <div style={{ width: '100%' }}>
                  <DataTable 
                    columns={columns} 
                    data={products} 
                    pagination 
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    onChangePage={page => {
                      setCurrentPage(page);
                      fetchProducts(page, perPage);
                    }}
                    onChangeRowsPerPage={(newPerPage, page) => {
                      setPerPage(newPerPage);
                      setCurrentPage(page);
                      fetchProducts(page, newPerPage);
                    }}
                    paginationComponentOptions={{
                      rowsPerPageText: t('rows_per_page', 'Filas por página:'),
                      rangeSeparatorText: t('of_range', 'de'),
                      selectAllRowsItem: false,
                    }}
                    customStyles={customStyles} 
                    progressPending={loading}
                    progressComponent={<LoadingState title={t('loading_catalogue', 'Cargando catálogo de productos...')} description={t('loading_catalogue_sub', 'Consultando inventario, precios base y categorías')} />}
                    noDataComponent={<div className="my-5 text-muted">{t('no_products_in_inventory', 'No hay productos en el inventario.')}</div>}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* CREATE MODAL */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Form onSubmit={handleCreateOrUpdate}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <Modal.Title>{editingId ? t('modal_edit_product', 'Editar Producto') : t('modal_create_product', 'Crear Nuevo Producto')}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: isDark ? '#11192e' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold' }}>{t('step_select_category', '1. Seleccionar Categoría')}</Form.Label>
                  <Form.Select value={formData.categoria_id} onChange={e => handleCategoryChange(e.target.value)} style={{ border: '2px solid #183eeb', borderRadius: '10px', backgroundColor: isDark ? '#0b1329' : '#f4f6ff', color: isDark ? '#ffffff' : '#010028' }}>
                    <option value="">{t('choose_category_placeholder', 'Seleccione Categoría')}</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.parent_id ? `\u00A0\u00A0↳ ${cat.nombre} (${cat.prefijo_sku || ''})` : `📁 ${cat.nombre.toUpperCase()}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_unit_measure', 'Unidad de Medida')}</Form.Label>
                  <Form.Select required value={formData.unidad_medida} onChange={e => setFormData({...formData, unidad_medida: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }}>
                    <option value="UND">UND (Unidad)</option>
                    <option value="KG">KG (Kilogramo)</option>
                    <option value="PQTE">PQTE (Paquete)</option>
                    <option value="BANDEJA">BANDEJA</option>
                    <option value="RISTRA">RISTRA</option>
                    <option value="DISPLAY">DISPLAY</option>
                    <option value="TRIPACK">TRIPACK</option>
                    <option value="BOLSA">BOLSA</option>
                    <option value="PAQUETE">PAQUETE</option>
                    <option value="CAJA">CAJA</option>
                    <option value="LITRO">LITRO</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_sku', 'SKU (Código)')}</Form.Label>
                  <Form.Control type="text" required value={formData.sku} onChange={e => setFormData({...formData, sku: e.target.value})} disabled={!!editingId} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} placeholder="ej: Q-0001 / A-0001" />
                </Form.Group>
              </div>
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_product_name', 'Nombre del Producto')}</Form.Label>
                  <Form.Control type="text" required value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('th_family_manufacturer', 'Familia (Fabricante / Casa Comercial)')}</Form.Label>
                  <Form.Control 
                    type="text" 
                    list="modal-families-datalist"
                    value={formData.familia} 
                    onChange={e => setFormData({...formData, familia: e.target.value})} 
                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                    placeholder="ej: Todo Import C.A. / Incaragua / L Prados" 
                  />
                  <datalist id="modal-families-datalist">
                    {families.map((fam, idx) => (
                      <option key={idx} value={fam} />
                    ))}
                  </datalist>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_brand', 'Marca Comercial (Línea Específica)')}</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={formData.marca} 
                    onChange={e => setFormData({...formData, marca: e.target.value})} 
                    style={{ border: '1px solid #183eeb', borderRadius: '10px' }} 
                    placeholder="ej: Americandy / Big Bom / Proembuti" 
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_price_usd', 'Precio USD ($)')}</Form.Label>
                  <Form.Control type="number" step="0.01" min="0" required value={formData.precio_base_usd} onChange={e => setFormData({...formData, precio_base_usd: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_units_per_box', 'Unidades por Caja / Bulto')}</Form.Label>
                  <Form.Control type="number" min="1" required value={formData.unidades_por_caja} onChange={e => setFormData({...formData, unidades_por_caja: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_physical_stock', 'Stock Físico')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_real} onChange={e => setFormData({...formData, stock_real: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_min_stock', 'Stock Mínimo')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_minimo} onChange={e => setFormData({...formData, stock_minimo: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_reserved_stock', 'Reservado')}</Form.Label>
                  <Form.Control type="number" min="0" required value={formData.stock_reservado} onChange={e => setFormData({...formData, stock_reservado: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
                </Form.Group>
              </div>
            </div>

            {/* Sección de Gestión de Imágenes Multicanal: B2C, B2B y Fotos en Reserva */}
            <div 
              className="mb-3 p-3 rounded" 
              style={{ 
                backgroundColor: isDark ? '#111936' : '#f8fafc', 
                border: isDark ? '1px solid #233160' : '1px solid #e2e8f0' 
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Form.Label className="mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', fontSize: '0.92rem' }}>
                  <i className="mdi mdi-image-multiple-outline me-1 text-primary"></i>
                  {t('field_product_images_multichannel', 'Imágenes del Producto (B2C / B2B)')}
                </Form.Label>
                <small style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                  Segmentación según canal comercial del cliente
                </small>
              </div>

              {/* 1. Tarjetas de Fotos Principales Activas en la App */}
              <div className="row g-2 mb-3">
                {/* 1.1 Imagen Principal B2C (Empaque / Detal) */}
                <div className="col-md-6">
                  <div 
                    className="p-2 rounded border h-100 d-flex flex-column justify-content-between"
                    style={{
                      backgroundColor: isDark ? '#161e38' : '#ffffff',
                      borderColor: isDark ? '#233160' : '#e2e8f0'
                    }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small fw-bold" style={{ fontSize: '0.78rem', color: isDark ? '#ffffff' : '#010028' }}>
                          <i className="mdi mdi-shopping me-1 text-info"></i> Principal B2C (Unidad)
                        </span>
                        <Badge bg="info" style={{ fontSize: '0.65rem' }}>B2C / DETAL</Badge>
                      </div>
                      <small className="d-block mb-2" style={{ fontSize: '0.69rem', lineHeight: '1.2', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Empaque individual visible para clientes de retail, bodegones y autoservicios.
                      </small>
                    </div>

                    <div>
                      {formData.imagen_b2c_url ? (
                        <div 
                          className="d-flex align-items-center gap-2 mb-2 p-1 rounded border"
                          style={{
                            backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                            borderColor: isDark ? '#233160' : '#e2e8f0'
                          }}
                        >
                          <img 
                            src={formData.imagen_b2c_url} 
                            alt="Preview B2C" 
                            style={{ 
                              width: '48px', 
                              height: '48px', 
                              borderRadius: '6px', 
                              objectFit: 'contain', 
                              backgroundColor: isDark ? '#11192e' : '#fff', 
                              border: isDark ? '1px solid #233160' : '1px solid #cbd5e1' 
                            }} 
                          />
                          <div className="text-truncate flex-grow-1">
                            <small className="text-truncate d-block fw-semibold" style={{ fontSize: '0.72rem', color: isDark ? '#ffffff' : '#010028' }}>
                              {formData.imagen_b2c_url.split('/').pop()}
                            </small>
                            <span className="text-success small fw-semibold" style={{ fontSize: '0.68rem' }}>
                              <i className="mdi mdi-check-circle me-1"></i>Activa en App
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="mb-2 p-2 rounded border text-center" 
                          style={{ 
                            fontSize: '0.72rem',
                            backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                            borderColor: isDark ? '#233160' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#64748b'
                          }}
                        >
                          Sin imagen B2C asignada (la app mostrará placeholder)
                        </div>
                      )}

                      <div className="d-flex gap-1 flex-wrap">
                        <Button 
                          size="sm" 
                          className="flex-fill" 
                          style={{ backgroundColor: '#0284c7', borderColor: '#0284c7', color: 'white', borderRadius: '8px', fontSize: '0.74rem', padding: '4px 6px' }}
                          onClick={() => openGallery('imagen_b2c_url')}
                        >
                          <i className="mdi mdi-image-search me-1"></i>
                          {formData.imagen_b2c_url ? 'Cambiar' : 'Seleccionar B2C'}
                        </Button>
                        {formData.imagen_b2c_url && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            style={{ 
                              borderRadius: '8px', 
                              fontSize: '0.72rem', 
                              padding: '4px 6px',
                              color: isDark ? '#cbd5e1' : undefined,
                              borderColor: isDark ? '#334155' : undefined
                            }}
                            onClick={handleMoveB2cToReserva}
                            title="Mover esta foto a la reserva (banca) y dejar B2C libre"
                          >
                            <i className="mdi mdi-archive-arrow-down-outline me-1"></i>A Reserva
                          </Button>
                        )}
                        {formData.imagen_b2c_url && (
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            style={{ borderRadius: '8px', padding: '4px 8px' }}
                            onClick={() => setFormData({ ...formData, imagen_b2c_url: '' })}
                            title="Quitar imagen B2C"
                          >
                            <i className="mdi mdi-delete"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1.2 Imagen Principal B2B (Caja / Mayorista) */}
                <div className="col-md-6">
                  <div 
                    className="p-2 rounded border h-100 d-flex flex-column justify-content-between"
                    style={{
                      backgroundColor: isDark ? '#161e38' : '#ffffff',
                      borderColor: isDark ? '#233160' : '#e2e8f0'
                    }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small fw-bold" style={{ fontSize: '0.78rem', color: isDark ? '#ffffff' : '#010028' }}>
                          <i className="mdi mdi-package-variant-closed me-1 text-primary"></i> Principal B2B (Caja)
                        </span>
                        <Badge bg="primary" style={{ backgroundColor: '#183eeb', fontSize: '0.65rem' }}>B2B / MAYOR</Badge>
                      </div>
                      <small className="d-block mb-2" style={{ fontSize: '0.69rem', lineHeight: '1.2', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Caja corrugada o bulto al mayor visible para clientes transformadores e industriales.
                      </small>
                    </div>

                    <div>
                      {formData.imagen_b2b_url ? (
                        <div 
                          className="d-flex align-items-center gap-2 mb-2 p-1 rounded border"
                          style={{
                            backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                            borderColor: isDark ? '#233160' : '#e2e8f0'
                          }}
                        >
                          <img 
                            src={formData.imagen_b2b_url} 
                            alt="Preview B2B" 
                            style={{ 
                              width: '48px', 
                              height: '48px', 
                              borderRadius: '6px', 
                              objectFit: 'contain', 
                              backgroundColor: isDark ? '#11192e' : '#fff', 
                              border: isDark ? '1px solid #233160' : '1px solid #cbd5e1' 
                            }} 
                          />
                          <div className="text-truncate flex-grow-1">
                            <small className="text-truncate d-block fw-semibold" style={{ fontSize: '0.72rem', color: isDark ? '#ffffff' : '#010028' }}>
                              {formData.imagen_b2b_url.split('/').pop()}
                            </small>
                            <span className="text-primary small fw-semibold" style={{ fontSize: '0.68rem' }}>
                              <i className="mdi mdi-check-circle me-1"></i>Activa en App
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="mb-2 p-2 rounded border text-center" 
                          style={{ 
                            fontSize: '0.72rem',
                            backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                            borderColor: isDark ? '#233160' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#64748b'
                          }}
                        >
                          Sin imagen B2B asignada (la app mostrará placeholder)
                        </div>
                      )}

                      <div className="d-flex gap-1 flex-wrap">
                        <Button 
                          size="sm" 
                          className="flex-fill" 
                          style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontSize: '0.74rem', padding: '4px 6px' }}
                          onClick={() => openGallery('imagen_b2b_url')}
                        >
                          <i className="mdi mdi-image-search me-1"></i>
                          {formData.imagen_b2b_url ? 'Cambiar' : 'Seleccionar B2B'}
                        </Button>
                        {formData.imagen_b2b_url && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            style={{ 
                              borderRadius: '8px', 
                              fontSize: '0.72rem', 
                              padding: '4px 6px',
                              color: isDark ? '#cbd5e1' : undefined,
                              borderColor: isDark ? '#334155' : undefined
                            }}
                            onClick={handleMoveB2bToReserva}
                            title="Mover esta foto a la reserva (banca) y dejar B2B libre"
                          >
                            <i className="mdi mdi-archive-arrow-down-outline me-1"></i>A Reserva
                          </Button>
                        )}
                        {formData.imagen_b2b_url && (
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            style={{ borderRadius: '8px', padding: '4px 8px' }}
                            onClick={() => setFormData({ ...formData, imagen_b2b_url: '' })}
                            title="Quitar imagen B2B"
                          >
                            <i className="mdi mdi-delete"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Fotos de Reserva ("En la Banca del Producto") */}
              <div 
                className="p-3 rounded border mb-3"
                style={{
                  backgroundColor: isDark ? '#161e38' : '#ffffff',
                  borderColor: isDark ? '#233160' : '#e2e8f0'
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <span className="small fw-bold d-block" style={{ fontSize: '0.82rem', color: isDark ? '#ffffff' : '#010028' }}>
                      <i className="mdi mdi-image-album me-1 text-warning"></i> Fotos de Reserva ("En la Banca")
                    </span>
                    <small style={{ fontSize: '0.69rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                      Fotos cargadas de este producto disponibles en el Backoffice. No son visibles en la app hasta que las promuevas a Principal B2C o B2B con un clic.
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="secondary" style={{ fontSize: '0.72rem' }}>
                      {(formData.galeria || []).length} en reserva
                    </Badge>
                    <Button 
                      size="sm" 
                      variant="outline-primary" 
                      style={{ borderRadius: '8px', fontSize: '0.72rem', padding: '3px 8px' }}
                      onClick={() => openGallery('galeria_add')}
                    >
                      <i className="mdi mdi-image-album me-1"></i>Agregar a Reserva
                    </Button>
                  </div>
                </div>

                {formData.galeria && formData.galeria.length > 0 ? (
                  <div className="row g-2 pt-1">
                    {formData.galeria.map((rImg, rIdx) => {
                      const rFileName = rImg.split('/').pop();
                      return (
                        <div key={rIdx} className="col-6 col-sm-4 col-md-3">
                          <div 
                            className="p-2 border rounded h-100 d-flex flex-column justify-content-between text-center position-relative"
                            style={{
                              backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                              borderColor: isDark ? '#233160' : '#e2e8f0'
                            }}
                          >
                            <Badge 
                              bg="dark" 
                              className="position-absolute top-0 start-0 m-1 shadow-sm" 
                              style={{ fontSize: '0.62rem', opacity: 0.85 }}
                            >
                              Reserva #{rIdx + 1}
                            </Badge>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0 position-absolute top-0 end-0 m-1"
                              onClick={() => handleRemoveFromReserva(rImg)}
                              title="Quitar de reserva"
                              style={{ lineHeight: 1 }}
                            >
                              <i className="mdi mdi-close-circle" style={{ fontSize: '1rem' }}></i>
                            </button>
                            <div 
                              className="my-2 d-flex align-items-center justify-content-center rounded p-1" 
                              style={{ 
                                height: '70px',
                                backgroundColor: isDark ? '#11192e' : '#ffffff',
                                border: isDark ? '1px solid #233160' : '1px solid #e2e8f0'
                              }}
                            >
                              <img 
                                src={rImg} 
                                alt={`Reserva ${rIdx + 1}`} 
                                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }} 
                              />
                            </div>
                            <small className="text-truncate d-block fw-semibold mb-2" title={rFileName} style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                              {rFileName}
                            </small>
                            <div className="d-flex gap-1 justify-content-center">
                              <Button 
                                size="sm" 
                                style={{ backgroundColor: '#0284c7', borderColor: '#0284c7', color: 'white', borderRadius: '6px', fontSize: '0.66rem', padding: '2px 5px' }}
                                onClick={() => handlePromoteToB2c(rImg)}
                                title="Promover esta foto a Principal B2C"
                              >
                                Usar B2C
                              </Button>
                              <Button 
                                size="sm" 
                                style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '6px', fontSize: '0.66rem', padding: '2px 5px' }}
                                onClick={() => handlePromoteToB2b(rImg)}
                                title="Promover esta foto a Principal B2B"
                              >
                                Usar B2B
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div 
                    className="p-3 text-center rounded border" 
                    style={{ 
                      fontSize: '0.74rem',
                      backgroundColor: isDark ? '#0b1329' : '#f8fafc',
                      borderColor: isDark ? '#233160' : '#e2e8f0',
                      color: isDark ? '#94a3b8' : '#64748b'
                    }}
                  >
                    <i className="mdi mdi-image-multiple-outline me-1"></i>
                    No hay fotos en la banca de reserva. Pulsa "Agregar a Reserva" para guardar fotos adicionales de este producto.
                  </div>
                )}
              </div>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#010028', fontWeight: 'bold' }}>{t('field_description', 'Descripción')}</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} style={{ border: '1px solid #183eeb', borderRadius: '10px' }} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: isDark ? '#11192e' : '#f8f9fa', borderTop: isDark ? '1px solid #233160' : '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)} style={{ color: isDark ? '#cbd5e1' : '#010028', borderColor: isDark ? '#334155' : '#010028', borderRadius: '10px' }}>{t('btn_cancel', 'Cancelar')}</Button>
            <Button type="submit" disabled={processing} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" /> : (editingId ? t('btn_save_changes', 'Guardar Cambios') : t('btn_save_product', 'Guardar Producto'))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* IMPORT MODAL */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
        <Form onSubmit={handleImport}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
            <div className="d-flex align-items-center">
              <i className="mdi mdi-file-excel me-2 fs-4 text-success"></i>
              <div>
                <Modal.Title className="h5 mb-0 fw-bold">{t('modal_import_products_title', 'Importación Masiva de Productos')}</Modal.Title>
                <small className="text-white-50" style={{ fontSize: '0.8rem' }}>{t('modal_import_products_subtitle', 'Carga de inventario y catálogo oficial ALPINCA')}</small>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff', padding: '24px' }}>
            {/* Step 1: Download official template */}
            <div className="p-3 mb-4 rounded border bg-light d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center">
                  <i className="mdi mdi-file-download-outline text-primary me-2 fs-5"></i>
                  {t('download_template_title', '1. Descargar Plantilla Oficial Excel')}
                </h6>
                <p className="text-muted small mb-0">
                  {t('download_template_desc', 'Descarga el archivo base (.xlsx) con los encabezados y ejemplos preconfigurados.')}
                </p>
              </div>
              <Button 
                variant="outline-primary" 
                onClick={handleDownloadTemplate}
                className="d-flex align-items-center shadow-sm"
                style={{ borderRadius: '10px', fontWeight: 'bold' }}
              >
                <i className="mdi mdi-microsoft-excel me-2 text-success fs-5"></i>
                {t('btn_download_template', 'Descargar Plantilla (.xlsx)')}
              </Button>
            </div>

            {/* Step 2: Info alert */}
            <div className="alert alert-info py-2 px-3 mb-4" style={{ borderRadius: '10px', fontSize: '0.85rem' }}>
              <div className="fw-bold mb-1">
                <i className="mdi mdi-information me-1 text-primary"></i>
                {t('import_columns_required', 'Columnas obligatorias en el archivo:')}
              </div>
              <code style={{ fontSize: '0.8rem', color: '#00024f' }}>
                Departamento | Subcategoría | Código SKU | Descripción / Nombre | Familia (Fabricante) | Marca | Unidad de Medida | Cantidad por Caja | Existencia | Precio Unitario ($)
              </code>
            </div>
            
            {/* Step 3: Drag & Drop upload area */}
            <div 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`text-center p-4 border-dashed rounded ${isDragging ? 'bg-light border-primary' : ''}`} 
              style={{ border: isDragging ? '2px dashed #183eeb' : '2px dashed #cbd5e1', borderRadius: '15px', backgroundColor: '#f8fafc', transition: 'all 0.2s ease' }}
            >
              <i className="mdi mdi-cloud-upload-outline d-block mb-2" style={{ fontSize: '42px', color: '#183eeb' }}></i>
              <div className="fw-bold mb-1" style={{ color: '#010028', fontSize: '1.05rem' }}>
                {t('select_inventory_file', 'Arrastra tu archivo Excel o CSV aquí')}
              </div>
              <p className="text-muted small mb-3">{t('supported_formats', 'Formatos soportados: .xlsx, .xls, .csv (Máx 20MB)')}</p>
              
              <Form.Control 
                type="file" 
                accept=".xlsx,.xls,.csv" 
                required={!importFile} 
                onChange={e => setImportFile(e.target.files[0])} 
                className="d-none"
                id="excel-catalog-upload"
              />
              <div>
                <Button as="label" htmlFor="excel-catalog-upload" style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px', cursor: 'pointer' }}>
                  <i className="mdi mdi-folder-open-outline me-1"></i> {t('btn_browse_files', 'Examinar Archivos')}
                </Button>
              </div>

              {importFile && (
                <div className="mt-3 text-success fw-bold p-2 bg-white rounded border d-inline-flex align-items-center shadow-sm">
                  <i className="mdi mdi-file-excel text-success me-2" style={{ fontSize: '1.3rem' }}></i>
                  <span>{importFile.name}</span>
                  <span className="text-muted small ms-2">({(importFile.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #00024f' }}>
            <Button variant="outline-secondary" onClick={() => { setShowImportModal(false); setImportFile(null); }} style={{ color: '#010028', borderColor: '#010028', borderRadius: '10px' }}>
              {t('btn_cancel', 'Cancelar')}
            </Button>
            <Button type="submit" disabled={processing || !importFile} style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px' }}>
              {processing ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="mdi mdi-upload me-1"></i>}
              {t('btn_upload_import', 'Subir e Importar')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* BULK IMPORT ERROR REPORT MODAL */}
      <Modal show={showImportErrorModal} onHide={() => setShowImportErrorModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#dc3545', color: '#ffffff', borderBottom: '3px solid #b02a37' }}>
          <Modal.Title className="d-flex align-items-center">
            <i className="mdi mdi-alert-rhombus me-2" style={{ fontSize: '1.5rem' }}></i>
            {t('modal_error_report_title', 'Reporte de Errores en Carga Masiva')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="danger" className="d-flex align-items-center mb-4" style={{ borderRadius: '10px' }}>
            <i className="mdi mdi-alert-circle me-3" style={{ fontSize: '2rem' }}></i>
            <div>
              <strong className="d-block mb-1">{t('import_rejected_title', 'La importación fue rechazada por completo (Rollback Transaccional)')}</strong>
              <span>{t('import_rejected_sub_prod', 'Se detectaron errores de validación o duplicados en las filas detalladas a continuación. Ningún producto fue guardado en el catálogo.')}</span>
            </div>
          </Alert>

          <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="align-middle mb-0">
              <thead className="sticky-top bg-light">
                <tr>
                  <th className="text-center" style={{ width: '80px' }}>{t('th_row', 'Fila #')}</th>
                  <th style={{ width: '200px' }}>{t('th_field', 'Campo')}</th>
                  <th>{t('th_reason', 'Motivo del Error')}</th>
                </tr>
              </thead>
              <tbody>
                {importErrors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="text-center fw-bold text-danger">
                      <Badge bg="danger" style={{ fontSize: '0.85rem' }}>{err.row || err.fila || '-'}</Badge>
                    </td>
                    <td className="fw-bold text-dark">{err.field || err.campo || 'General'}</td>
                    <td className="text-danger font-weight-bold">{err.reason || err.motivo || err.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fc', borderTop: '1px solid #dee2e6' }}>
          <Button 
            variant="secondary" 
            onClick={() => { setShowImportErrorModal(false); setShowImportModal(true); }}
            style={{ borderRadius: '10px' }}
          >
            <i className="mdi mdi-refresh me-1"></i> {t('btn_fix_and_retry', 'Corregir Archivo y Reintentar')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Gallery Modal */}
      <Modal show={showGalleryModal} onHide={() => setShowGalleryModal(false)} size="xl" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-image-multiple me-2 fs-4 text-primary"></i>
            <div>
              <Modal.Title className="h5 mb-0 fw-bold">
                {galleryTargetField === 'imagen_b2c_url' 
                  ? 'Seleccionar Imagen Principal B2C (Empaque / Detal)' 
                  : (galleryTargetField === 'imagen_b2b_url' 
                      ? 'Seleccionar Imagen Principal B2B (Caja / Mayorista)' 
                      : (galleryTargetField === 'galeria_add'
                          ? 'Agregar Foto a la Reserva ("En la Banca")'
                          : t('modal_gallery_select_title', 'Galería Multimedia de Catálogo')))}
              </Modal.Title>
              <small className="text-white-50" style={{ fontSize: '0.8rem' }}>
                {galleryTargetField === 'imagen_b2c_url'
                  ? 'Haz clic sobre la imagen del empaque individual (retail) para este producto'
                  : (galleryTargetField === 'imagen_b2b_url'
                      ? 'Haz clic sobre la imagen de la caja corrugada o bulto al mayor para este producto'
                      : (galleryTargetField === 'galeria_add'
                          ? 'Haz clic sobre una foto para agregarla al pool de reserva de este producto'
                          : t('gallery_select_subtitle', 'Haz clic sobre la imagen que deseas asignar a este producto')))}
              </small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: isDark ? '#11192e' : '#f8fafc', padding: '20px' }}>
          {/* Top Action & Search Bar */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1" style={{ maxWidth: '700px' }}>
              <div className="position-relative" style={{ minWidth: '240px', flex: 1 }}>
                <i 
                  className="mdi mdi-magnify position-absolute text-muted" 
                  style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.1rem', zIndex: 5, pointerEvents: 'none' }}
                />
                <Form.Control
                  type="text"
                  placeholder={t('search_file_placeholder', 'Buscar por nombre de archivo o SKU...')}
                  value={gallerySearch}
                  onChange={e => setGallerySearch(e.target.value)}
                  className="w-100 shadow-none"
                  style={{
                    paddingLeft: '36px',
                    paddingRight: '12px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    height: '40px',
                    lineHeight: '1.4',
                    marginTop: 0,
                    marginBottom: 0,
                    borderRadius: '8px',
                    border: isDark ? '1.5px solid #334155' : '1.5px solid #cbd5e1',
                    backgroundColor: isDark ? '#161e38' : '#ffffff',
                    color: isDark ? '#ffffff' : '#010028',
                    fontSize: '0.85rem'
                  }}
                />
              </div>

              {/* Filtro por Producto */}
              <div style={{ minWidth: '250px' }}>
                <Form.Select
                  value={galleryProductFilter}
                  onChange={e => setGalleryProductFilter(e.target.value)}
                  className="shadow-none"
                  style={{
                    height: '40px',
                    lineHeight: '1.4',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    paddingLeft: '12px',
                    paddingRight: '36px',
                    marginTop: 0,
                    marginBottom: 0,
                    borderRadius: '8px',
                    border: isDark ? '1.5px solid #334155' : '1.5px solid #cbd5e1',
                    fontSize: '0.84rem',
                    fontWeight: '500',
                    backgroundColor: isDark ? '#161e38' : '#ffffff',
                    color: isDark ? '#ffffff' : '#010028'
                  }}
                >
                  <option value="">Todas las imágenes ({galleryImages.length})</option>
                  {formData.sku && (
                    <option value="current">Solo este producto ({formData.sku})</option>
                  )}
                  {formData.sku && (
                    <option value="reserva">En Reserva ({formData.sku})</option>
                  )}
                  <option value="unassigned">Disponibles (Sin asignar)</option>
                </Form.Select>
              </div>
            </div>

            <div>
              <input
                type="file"
                multiple
                accept="image/png, image/jpeg, image/webp"
                style={{ display: 'none' }}
                ref={galleryFileInputRef}
                onChange={handleGalleryUpload}
              />
              <Button
                size="sm"
                style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '8px', fontWeight: 'bold', height: '38px' }}
                onClick={() => galleryFileInputRef.current && galleryFileInputRef.current.click()}
                disabled={uploadingGallery || loadingGallery}
              >
                {uploadingGallery ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="mdi mdi-cloud-upload me-1"></i>}
                {uploadingGallery ? t('uploading_label', 'Subiendo...') : t('btn_upload_new_image', 'Subir Nueva Imagen')}
              </Button>
            </div>
          </div>

          {/* Gallery Content Area */}
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
            {loadingGallery ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                <h6 className="mt-3 fw-bold" style={{ color: '#00024f' }}>{t('loading_media_gallery', 'Cargando galería multimedia...')}</h6>
                <p className="text-muted small">{t('querying_server_images', 'Consultando imágenes del servidor central Rutex')}</p>
              </div>
            ) : (() => {
              const filtered = galleryImages.filter(img => {
                const srcLower = (img.src || '').toLowerCase();
                const filenameLower = (img.filename || '').toLowerCase();
                const queryLower = gallerySearch.toLowerCase();
                
                if (gallerySearch && !srcLower.includes(queryLower) && !filenameLower.includes(queryLower)) {
                  return false;
                }

                if (galleryProductFilter === 'current') {
                  const isAssignedCurrent = img.assigned_to && img.assigned_to.id === editingId;
                  const cleanSku = (formData.sku || '').toLowerCase().replace(/[\/\-_]/g, '');
                  const cleanFile = filenameLower.replace(/[\/\-_]/g, '');
                  const skuInName = cleanSku && cleanFile.includes(cleanSku);
                  const isInGaleria = (formData.galeria || []).includes(img.src);
                  const isCurrentB2c = formData.imagen_b2c_url === img.src;
                  const isCurrentB2b = formData.imagen_b2b_url === img.src;
                  return isAssignedCurrent || skuInName || isInGaleria || isCurrentB2c || isCurrentB2b;
                }

                if (galleryProductFilter === 'reserva') {
                  const isInGaleria = (formData.galeria || []).includes(img.src);
                  const isAssignedReserva = img.assigned_to && img.assigned_to.id === editingId && (img.assigned_to.tipo === 'reserva' || img.assigned_to.tipo === 'galeria');
                  return isInGaleria || isAssignedReserva;
                }

                if (galleryProductFilter === 'unassigned') {
                  return !img.is_in_use && !img.assigned_to;
                }

                return true;
              });

              if (filtered.length === 0) {
                return (
                  <div className="text-center py-5">
                    <i className="mdi mdi-image-off" style={{ fontSize: '48px', color: '#94a3b8' }}></i>
                    <h6 className="mt-2 fw-bold text-dark">{t('no_images_found_gallery', 'No se encontraron imágenes')}</h6>
                    <p className="text-muted small mb-3">
                      {gallerySearch ? t('no_images_found_gallery', 'No hay imágenes que coincidan con la búsqueda.') : (galleryProductFilter === 'current' ? `No se encontraron imágenes asignadas al SKU ${formData.sku}.` : t('no_images_in_gallery_system', 'La galería del servidor está vacía.'))}
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline-primary" 
                      style={{ borderRadius: '8px' }}
                      onClick={() => galleryFileInputRef.current && galleryFileInputRef.current.click()}
                    >
                      <i className="mdi mdi-upload me-1"></i> {t('btn_upload_now', 'Subir imágenes ahora')}
                    </Button>
                  </div>
                );
              }

              return (
                <Row className="g-3">
                  {filtered.map((img, idx) => {
                    const fileName = img.filename || img.src.split('/').pop();
                    const isUsedByOther = img.assigned_to && img.assigned_to.id !== editingId;
                    const isCurrentTarget = formData[galleryTargetField] === img.src;
                    const isB2c = formData.imagen_b2c_url === img.src || (img.assigned_to && img.assigned_to.id === editingId && img.assigned_to.tipo === 'b2c');
                    const isB2b = formData.imagen_b2b_url === img.src || (img.assigned_to && img.assigned_to.id === editingId && img.assigned_to.tipo === 'b2b');
                    const isInReserva = (formData.galeria || []).includes(img.src) || (img.assigned_to && img.assigned_to.id === editingId && (img.assigned_to.tipo === 'reserva' || img.assigned_to.tipo === 'galeria'));
                    const isAvailable = !img.is_in_use && !img.assigned_to;

                    return (
                      <Col xs={6} sm={4} md={3} lg={3} key={idx}>
                        <Card 
                          className={`h-100 shadow-sm ${isCurrentTarget ? 'border-primary' : (isUsedByOther ? 'border-danger' : 'border-0')}`}
                          style={{ 
                            cursor: isUsedByOther ? 'not-allowed' : 'pointer', 
                            opacity: isUsedByOther ? 0.75 : 1,
                            transition: 'all 0.2s ease', 
                            borderRadius: '12px',
                            overflow: 'hidden',
                            backgroundColor: isUsedByOther ? (isDark ? '#2e1515' : '#fff8f8') : (isDark ? '#161e38' : '#ffffff'),
                            border: isCurrentTarget ? '2px solid #183eeb' : (isUsedByOther ? (isDark ? '1.5px solid #7f1d1d' : '1.5px solid #fca5a5') : (isDark ? '1px solid #233160' : '1px solid #e2e8f0'))
                          }}
                          onClick={() => !isUsedByOther && selectGalleryImage(img)}
                          onMouseEnter={e => {
                            if (!isUsedByOther) {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.borderColor = '#183eeb';
                              e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 62, 235, 0.18)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isUsedByOther) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.borderColor = isCurrentTarget ? '#183eeb' : (isDark ? '#233160' : '#e2e8f0');
                              e.currentTarget.style.boxShadow = '';
                            }
                          }}
                        >
                          <div style={{ height: '130px', backgroundColor: isDark ? '#0b1329' : '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', position: 'relative', borderBottom: isDark ? '1px solid #233160' : '1px solid #f1f5f9' }}>
                            <img 
                              src={img.src} 
                              alt={fileName} 
                              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            {isCurrentTarget && (
                              <Badge bg="primary" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ backgroundColor: '#183eeb', borderRadius: '6px', fontSize: '0.68rem' }}>
                                <i className="mdi mdi-check-circle me-1"></i> Asignada
                              </Badge>
                            )}
                            {!isCurrentTarget && isB2c && (
                              <Badge bg="info" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ borderRadius: '6px', fontSize: '0.68rem' }}>
                                <i className="mdi mdi-shopping me-1"></i> Principal B2C
                              </Badge>
                            )}
                            {!isCurrentTarget && !isB2c && isB2b && (
                              <Badge bg="primary" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ backgroundColor: '#183eeb', borderRadius: '6px', fontSize: '0.68rem' }}>
                                <i className="mdi mdi-package-variant-closed me-1"></i> Principal B2B
                              </Badge>
                            )}
                            {isInReserva && !isCurrentTarget && !isB2c && !isB2b && (
                              <Badge bg="dark" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ backgroundColor: '#00024f', borderRadius: '6px', fontSize: '0.68rem' }}>
                                <i className="mdi mdi-image-album me-1"></i> En Reserva
                              </Badge>
                            )}
                            {isAvailable && !isCurrentTarget && !isB2c && !isB2b && !isInReserva && (
                              <Badge bg="success" className="position-absolute top-0 end-0 m-2 shadow-sm" style={{ borderRadius: '6px', fontSize: '0.65rem' }}>
                                <i className="mdi mdi-check me-1"></i> Disponible
                              </Badge>
                            )}
                            {isUsedByOther && (
                              <Badge bg="danger" className="position-absolute top-0 start-0 m-2 shadow-sm" style={{ borderRadius: '6px', fontSize: '0.68rem' }} title={`En uso por SKU: ${img.assigned_to.sku}`}>
                                <i className="mdi mdi-lock me-1"></i> {img.assigned_to.sku}
                              </Badge>
                            )}
                          </div>
                          <Card.Footer className="p-2 text-center border-0" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff' }}>
                            <small className="text-truncate d-block fw-semibold mb-1" title={fileName} style={{ fontSize: '0.76rem', color: isDark ? '#ffffff' : '#010028' }}>
                              {fileName}
                            </small>
                            {isUsedByOther ? (
                              <small className="text-danger d-block text-truncate fw-bold" style={{ fontSize: '0.68rem' }}>
                                Asignada a {img.assigned_to.sku}
                              </small>
                            ) : (
                              <div className="d-flex gap-1 justify-content-center mt-1" onClick={e => e.stopPropagation()}>
                                <Button
                                  size="sm"
                                  style={{ backgroundColor: '#0284c7', borderColor: '#0284c7', color: 'white', borderRadius: '4px', fontSize: '0.62rem', padding: '2px 4px' }}
                                  onClick={() => selectGalleryImage(img, 'b2c')}
                                  title="Asignar como Principal B2C"
                                >
                                  B2C
                                </Button>
                                <Button
                                  size="sm"
                                  style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '4px', fontSize: '0.62rem', padding: '2px 4px' }}
                                  onClick={() => selectGalleryImage(img, 'b2b')}
                                  title="Asignar como Principal B2B"
                                >
                                  B2B
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  style={{ borderRadius: '4px', fontSize: '0.62rem', padding: '2px 4px' }}
                                  onClick={() => selectGalleryImage(img, 'reserva')}
                                  title="Agregar a fotos de reserva"
                                >
                                  Reserva
                                </Button>
                              </div>
                            )}
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              );
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: isDark ? '#11192e' : '#f8f9fa', borderTop: isDark ? '1px solid #233160' : '1px solid #e2e8f0' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowGalleryModal(false)} style={{ borderRadius: '8px' }}>
            {t('btn_close', 'Cerrar')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default Catalogue;